import React, { useState, useEffect, useRef } from 'react';
import { 
  TrendingUp, 
  Building2, 
  DollarSign,
  LucideIndianRupee,
  Users, 
  Award, 
  Lightbulb, 
  FileCheck, 
  GraduationCap, 
  Target, 
  CheckCircle, 
  Sprout,
  Coins
} from 'lucide-react';

// Consolidated Data from Brochure & Live Site
const countdata = [
  { 
    ct: 300, 
    title: 'Startups Incubated', 
    icon: Building2,
    desc: "Total ventures supported",
    color: "from-blue-500 to-cyan-400"
  },
  { 
    ct: 1870, 
    title: 'Total Valuation (Cr)', 
    icon: TrendingUp,
    desc: "Collective ecosystem value",
    color: "from-emerald-500 to-teal-400"
  },
  { 
    ct: 350, 
    title: 'External Funding (Cr)', 
    icon: LucideIndianRupee,
    desc: "Raised by startups",
    color: "from-amber-500 to-orange-400"
  },
  { 
    ct: 180, 
    title: 'Mentors Network', 
    icon: Users,
    desc: "Industry & Faculty experts",
    color: "from-purple-500 to-indigo-400"
  },
  { 
    ct: 120, 
    title: 'Funded Startups', 
    icon: Award, 
    desc: "Raised external capital",
    color: "from-indigo-500 to-blue-400" 
  },
  { 
    ct: 7, 
    title: 'CSR Funding (Cr)', 
    icon: Coins, 
    desc: "Corporate Social Responsibility",
    color: "from-rose-500 to-pink-400" 
  },
  { 
    ct: 75, 
    title: 'Seed Supported', 
    icon: Sprout, 
    desc: "Direct funding support",
    color: "from-lime-500 to-green-400" 
  },
  { 
    ct: 55, 
    title: 'Graduated', 
    icon: GraduationCap, 
    desc: "Successfully exited",
    color: "from-cyan-500 to-sky-400" 
  },
  { 
    ct: 28, 
    title: 'IPs Granted', 
    icon: CheckCircle, 
    desc: "Patents awarded",
    color: "from-teal-500 to-green-400" 
  },
  { 
    ct: 67, 
    title: 'IPs Filed', 
    icon: FileCheck, 
    desc: "In the pipeline",
    color: "from-orange-500 to-red-400" 
  },

];

function Achievements() {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedValues, setAnimatedValues] = useState({});
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    
    countdata.forEach((item, index) => {
      let start = 0;
      const end = item.ct;
      const duration = 2500; 
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          start = end;
          clearInterval(timer);
        }
        setAnimatedValues(prev => ({ ...prev, [index]: Math.floor(start) }));
      }, 16);

      return () => clearInterval(timer);
    });
  }, [isVisible]);

  return (
    <div ref={sectionRef} className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {countdata.map((data, idx) => {
          const animatedValue = animatedValues[idx] || 0;
          
          return (
            <div 
              key={idx} 
              className="group relative bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500 overflow-hidden hover:-translate-y-2"
            >
              {/* Background Gradient Blob */}
              <div className={`absolute -right-6 -top-6 w-32 h-32 bg-gradient-to-br ${data.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity duration-500`}></div>
              
              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${data.color} flex items-center justify-center text-white shadow-lg shadow-blue-500/20 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <data.icon size={28} strokeWidth={2} />
                  </div>
                  
                  <div className="flex items-baseline space-x-1">
                    <span className={`text-5xl font-black bg-gradient-to-r ${data.color} bg-clip-text text-transparent`}>
                      {animatedValue}
                    </span>
                    <span className="text-2xl font-bold text-slate-400">+</span>
                    {/* Unit for Crores */}
                    {data.title.includes('(Cr)') && (
                       <span className="text-lg font-bold text-slate-400 ml-1">Cr</span>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-50">
                  <h3 className="font-bold text-slate-800 text-lg leading-tight mb-1">
                    {data.title.replace(' (Cr)', '')}
                  </h3>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    {data.desc}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Achievements;